import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Chip, Button, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, Divider, LinearProgress, FormControl,
  InputLabel, Select, MenuItem, IconButton, Tooltip, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import Layout from '../components/Layout';
import { projectService } from '../services/api';
import api from '../services/api';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

const CATEGORIES = ['Site', 'Structure', 'MEP', 'Exterior', 'Interior', 'Materials', 'Soft Costs', 'Contingency', 'Other'];

const fmt = (n) => `$${Number(n || 0).toLocaleString()}`;
const pct = (a, b) => b > 0 ? Math.min((Number(a) / Number(b)) * 100, 100).toFixed(0) : 0;

export default function Budget() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [budget, setBudget] = useState({ cost_codes: [], totals: {} });
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, code: null });
  const [editingRows, setEditingRows] = useState({});
  const [massEditMode, setMassEditMode] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', category: '', budget_amount: '', contingency_amount: '', forecasted_amount: '' });

  useEffect(() => { fetchProjects(); }, []);
  useEffect(() => { if (selectedProject) fetchBudget(); }, [selectedProject]);

  const fetchProjects = async () => {
    const res = await projectService.getAll();
    setProjects(res.data);
    if (res.data.length > 0) setSelectedProject(res.data[0].id);
  };

  const fetchBudget = async () => {
    try {
      const res = await api.get(`/budget/project/${selectedProject}`);
      setBudget(res.data);
    } catch (err) {}
  };

  const handleSeed = async () => {
    setLoading(true);
    try {
      await api.post('/budget/cost-codes/seed', { project_id: selectedProject });
      showAlert('Default cost codes loaded!', 'success');
      fetchBudget();
    } catch (err) { showAlert('Failed to seed', 'error'); }
    setLoading(false);
  };

  const handleAdd = async () => {
    if (!form.code || !form.name) { showAlert('Code and name are required', 'error'); return; }
    setLoading(true);
    try {
      await api.post('/budget/cost-codes', { ...form, project_id: selectedProject });
      setAddDialog(false);
      setForm({ code: '', name: '', category: '', budget_amount: '', contingency_amount: '', forecasted_amount: '' });
      showAlert('Cost code added!', 'success');
      fetchBudget();
    } catch (err) { showAlert('Failed to add cost code', 'error'); }
    setLoading(false);
  };

  const handleMassSave = async () => {
    const updates = Object.entries(editingRows);
    if (updates.length === 0) { setMassEditMode(false); return; }
    try {
      const allCodes = budget.cost_codes;
      await Promise.all(updates.map(([id, vals]) => {
        const orig = allCodes.find(c => c.id === id) || {};
        return api.put(`/budget/cost-codes/${id}`, {
          code: orig.code,
          name: orig.name,
          category: orig.category,
          sort_order: orig.sort_order || 0,
          budget_amount: vals.budget_amount ?? orig.budget_amount,
          contingency_amount: vals.contingency_amount ?? orig.contingency_amount,
          forecasted_amount: vals.forecasted_amount ?? orig.forecasted_amount,
        });
      }));
      const res = await api.get(`/budget/cost-codes?project_id=${selectedProject}`);
      setBudget(prev => ({ ...prev, cost_codes: Array.isArray(res.data) ? res.data : [] }));
      setEditingRows({});
      setMassEditMode(false);
    } catch(e) { console.error(e); }
  };

  const handleEdit = async () => {
    setLoading(true);
    try {
      await api.put(`/budget/cost-codes/${editDialog.code.id}`, editDialog.code);
      setEditDialog({ open: false, code: null });
      showAlert('Cost code updated!', 'success');
      fetchBudget();
    } catch (err) { showAlert('Failed to update', 'error'); }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this cost code?')) return;
    try {
      await api.delete(`/budget/cost-codes/${id}`);
      showAlert('Cost code deactivated', 'success');
      fetchBudget();
    } catch (err) { showAlert('Failed to deactivate', 'error'); }
  };

  const showAlert = (message, severity) => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: '', severity: 'success' }), 4000);
  };

  const { totals } = budget;
  const overBudget = Number(totals.committed || 0) > Number(totals.budget || 0);
  const variance = Number(totals.budget || 0) - Number(totals.forecasted || totals.committed || 0);

  // Group by category
  const grouped = budget.cost_codes.reduce((acc, cc) => {
    const cat = cc.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(cc);
    return acc;
  }, {});

  return (
    <Layout>
      {alert.show && <Alert severity={alert.severity} sx={{ mb: 3, borderRadius: 2 }}>{alert.message}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a237e' }}>Budget Tracker</Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, fontSize: 15 }} sx={{ color: '#666' }}>Track costs by category across your project</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel>Project</InputLabel>
            <Select value={selectedProject} onChange={e => setSelectedProject(e.target.value)}
              label="Project" sx={{ borderRadius: 2 }}>
              {projects.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </Select>
          </FormControl>
          {budget.cost_codes.length === 0 && (
            <Button variant="outlined" startIcon={<AutoAwesomeIcon />} onClick={handleSeed} disabled={loading}
              sx={{ borderRadius: 2, borderColor: '#1a237e', color: '#1a237e' }}>
              Load Defaults
            </Button>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddDialog(true)}
            sx={{ bgcolor: '#1a237e', borderRadius: 2 }}>
            Add Code
          </Button>
          {massEditMode ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" size="small" onClick={handleMassSave}
                sx={{ bgcolor: '#2e7d32', borderRadius: 2 }}>Save All</Button>
              <Button variant="outlined" size="small" onClick={() => { setMassEditMode(false); setEditingRows({}); }}
                sx={{ borderColor: '#666', color: '#666', borderRadius: 2 }}>Cancel</Button>
            </Box>
          ) : (
            <Button variant="outlined" size="small" onClick={() => setMassEditMode(true)}
              sx={{ borderColor: '#1a237e', color: '#1a237e', borderRadius: 2 }}>✏️ Mass Edit</Button>
          )}
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Budget', value: fmt(totals.budget), sub: `+ ${fmt(totals.contingency)} contingency`, bg: 'linear-gradient(135deg, #1a237e, #283593)' },
          { label: 'Committed (POs)', value: fmt(totals.committed), sub: `${pct(totals.committed, totals.budget)}% of budget`, bg: 'linear-gradient(135deg, #6a1b9a, #8e24aa)' },
          { label: 'Actual (Paid)', value: fmt(totals.actual), sub: `${pct(totals.actual, totals.budget)}% of budget`, bg: 'linear-gradient(135deg, #2e7d32, #43a047)' },
          { label: 'Variance', value: fmt(Math.abs(variance)), sub: variance >= 0 ? '✅ Under budget' : '⚠️ Over budget', bg: variance >= 0 ? 'linear-gradient(135deg, #2e7d32, #43a047)' : 'linear-gradient(135deg, #c62828, #e53935)' },
        ].map((card, i) => (
          <Grid item xs={6} md={3} key={i}>
            <Card elevation={0} sx={{ borderRadius: 3, background: card.bg, color: 'white' }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>{card.value}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: 15 }} sx={{ opacity: 0.9, fontWeight: 600 }}>{card.label}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>{card.sub}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Budget Progress Bar */}
      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e8eaf6', mb: 3, p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, color: '#1a237e', mb: 2, fontSize: 20 }}>Overall Budget Health</Typography>
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e', fontSize: 18, fontWeight: 700 }}>Committed {pct(totals.committed, totals.budget)}%</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: 18, fontWeight: 700 }}>Paid {pct(totals.actual, totals.budget)}%</Typography>
          </Box>
          <Box sx={{ position: 'relative', height: 16, bgcolor: '#f5f6fa', borderRadius: 8 }}>
            <Box sx={{ position: 'absolute', height: '100%', width: `${pct(totals.committed, totals.budget)}%`, bgcolor: '#7b1fa2', borderRadius: 8, opacity: 0.4 }} />
            <Box sx={{ position: 'absolute', height: '100%', width: `${pct(totals.invoiced, totals.budget)}%`, bgcolor: '#e65100', borderRadius: 8, opacity: 0.6 }} />
            <Box sx={{ position: 'absolute', height: '100%', width: `${pct(totals.actual, totals.budget)}%`, bgcolor: '#2e7d32', borderRadius: 8 }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            {[
              { color: '#7b1fa2', label: 'Committed' },
              { color: '#e65100', label: 'Invoiced' },
              { color: '#2e7d32', label: 'Paid' },
            ].map(item => (
              <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color }} />
                <Typography variant="body2" sx={{ color: '#666', fontWeight: 600, fontSize: 13 }}>{item.label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Card>

      {/* Cost Code Table by Category */}
      {budget.cost_codes.length === 0 ? (
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e8eaf6', p: 6, textAlign: 'center' }}>
          <AccountBalanceWalletIcon sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#999' }}>No cost codes yet</Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, fontSize: 15 }} sx={{ color: '#ccc', mb: 2 }}>Click "Load Defaults" to start with standard construction categories</Typography>
          <Button variant="contained" startIcon={<AutoAwesomeIcon />} onClick={handleSeed}
            sx={{ bgcolor: '#1a237e', borderRadius: 2 }}>Load Default Cost Codes</Button>
        </Card>
      ) : (
        Object.entries(grouped).map(([category, codes]) => {
          const catBudget = codes.reduce((s, c) => s + Number(c.budget_amount), 0);
          const catCommitted = codes.reduce((s, c) => s + Number(c.committed_amount), 0);
          const catActual = codes.reduce((s, c) => s + Number(c.actual_amount), 0);
          return (
            <Card key={category} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e8eaf6', mb: 2 }}>
              <Box sx={{ px: 3, py: 1.5, bgcolor: '#f5f6fa', borderRadius: '12px 12px 0 0',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a237e' }}>{category}</Typography>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Typography variant="caption" sx={{ color: '#666' }}>Budget: <strong>{fmt(catBudget)}</strong></Typography>
                  <Typography variant="caption" sx={{ color: '#7b1fa2' }}>Committed: <strong>{fmt(catCommitted)}</strong></Typography>
                  <Typography variant="caption" sx={{ color: '#2e7d32' }}>Paid: <strong>{fmt(catActual)}</strong></Typography>
                </Box>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#fafafa' }}>
                      {['Code', 'Name', 'Budget', 'Contingency', 'Committed', 'Invoiced', 'Actual (Paid)', ''].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: 16, fontWeight: 700, color: '#555', py: 1 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {codes.map(cc => {
                      const budget_n = Number(cc.budget_amount);
                      const committed_n = Number(cc.committed_amount);
                      const actual_n = Number(cc.actual_amount);
                      const forecasted_n = Number(cc.forecasted_amount) || committed_n;
                      const variance_n = budget_n - forecasted_n;
                      const isOver = variance_n < 0;
                      return (
                        <TableRow key={cc.id} sx={{ '&:hover': { bgcolor: '#f5f6fa' } }}>
                          <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 16, fontWeight: 700 }}>{cc.code}</TableCell>
                          <TableCell sx={{ fontSize: 16, fontWeight: 700 }}>{cc.name}</TableCell>
                          <TableCell sx={{ fontSize: 16, fontWeight: 700 }}>
                            {massEditMode ? (
                              <input type="number" value={editingRows[cc.id]?.budget_amount ?? cc.budget_amount}
                                onChange={e => setEditingRows(r => ({...r, [cc.id]: {...(r[cc.id]||{}), budget_amount: e.target.value}}))}
                                style={{ width: 90, padding: '4px 6px', border: '1px solid #c5cae9', borderRadius: 4, fontSize: 16, fontWeight: 700 }}/>
                            ) : fmt(cc.budget_amount)}
                          </TableCell>
                          <TableCell sx={{ fontSize: 16, fontWeight: 700, color: '#666' }}>
                            {massEditMode ? (
                              <input type="number" value={editingRows[cc.id]?.contingency_amount ?? cc.contingency_amount}
                                onChange={e => setEditingRows(r => ({...r, [cc.id]: {...(r[cc.id]||{}), contingency_amount: e.target.value}}))}
                                style={{ width: 90, padding: '4px 6px', border: '1px solid #c5cae9', borderRadius: 4, fontSize: 16, fontWeight: 700 }}/>
                            ) : fmt(cc.contingency_amount)}
                          </TableCell>
                          <TableCell sx={{ fontSize: 16, fontWeight: 700, color: '#7b1fa2' }}>{fmt(cc.committed_amount)}</TableCell>
                          <TableCell sx={{ fontSize: 16, fontWeight: 700, color: '#e65100' }}>{fmt(cc.invoiced_amount)}</TableCell>
                          <TableCell sx={{ fontSize: 16, fontWeight: 700, color: '#2e7d32' }}>{fmt(cc.actual_amount)}</TableCell>


                          <TableCell>
                            <Box sx={{ display: 'flex' }}>
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => setEditDialog({ open: true, code: {...cc} })}>
                                  <EditIcon fontSize="small" sx={{ color: '#1a237e' }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Deactivate">
                                <IconButton size="small" onClick={() => handleDelete(cc.id)}>
                                  <DeleteIcon fontSize="small" sx={{ color: '#e53935' }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          );
        })
      )}

      {/* Add Cost Code Dialog */}
      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: '#1a237e', color: 'white', fontWeight: 700 }}>Add Cost Code</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Code *" value={form.code} onChange={e => setForm({...form, code: e.target.value})}
                sx={{ width: 120, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              <TextField fullWidth label="Name *" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                label="Category" sx={{ borderRadius: 2 }}>
                {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth label="Budget Amount ($)" type="number" value={form.budget_amount}
                onChange={e => setForm({...form, budget_amount: e.target.value})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              <TextField fullWidth label="Contingency ($)" type="number" value={form.contingency_amount}
                onChange={e => setForm({...form, contingency_amount: e.target.value})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <TextField fullWidth label="Forecasted Final Cost ($)" type="number" value={form.forecasted_amount}
              onChange={e => setForm({...form, forecasted_amount: e.target.value})}
              helperText="Leave blank to use committed amount as forecast"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={() => setAddDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={loading}
            sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>
            {loading ? 'Adding...' : 'Add Cost Code'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, code: null })} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: '#1a237e', color: 'white', fontWeight: 700 }}>Edit Cost Code</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {editDialog.code && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="Code *" value={editDialog.code.code}
                  onChange={e => setEditDialog({...editDialog, code: {...editDialog.code, code: e.target.value}})}
                  sx={{ width: 120, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                <TextField fullWidth label="Name *" value={editDialog.code.name}
                  onChange={e => setEditDialog({...editDialog, code: {...editDialog.code, name: e.target.value}})}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Box>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select value={editDialog.code.category || ''}
                  onChange={e => setEditDialog({...editDialog, code: {...editDialog.code, category: e.target.value}})}
                  label="Category" sx={{ borderRadius: 2 }}>
                  {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField fullWidth label="Budget Amount ($)" type="number" value={editDialog.code.budget_amount}
                  onChange={e => setEditDialog({...editDialog, code: {...editDialog.code, budget_amount: e.target.value}})}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                <TextField fullWidth label="Contingency ($)" type="number" value={editDialog.code.contingency_amount}
                  onChange={e => setEditDialog({...editDialog, code: {...editDialog.code, contingency_amount: e.target.value}})}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Box>
              <TextField fullWidth label="Forecasted Final Cost ($)" type="number" value={editDialog.code.forecasted_amount}
                onChange={e => setEditDialog({...editDialog, code: {...editDialog.code, forecasted_amount: e.target.value}})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={() => setEditDialog({ open: false, code: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit} disabled={loading}
            sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
